import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from './Navbar';
import Footer from './Footer';
import authService from '../services/authService';
import { useCity } from '../contexts/CityContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CATEGORY_CHIPS = [
  { label: 'Strategy 🧩', value: 'strategy' },
  { label: 'Party 🎉', value: 'party' },
  { label: 'RPG 🎲', value: 'rpg' },
  { label: 'Card Games 🃏', value: 'card' },
  { label: 'Co-op 🤝', value: 'coop' },
  { label: 'Tournaments 🏆', value: 'tournament' },
  { label: 'Casual 😊', value: 'casual' },
];

const DATE_CHIPS = [
  { label: 'Today', value: 'today' },
  { label: 'Tomorrow', value: 'tomorrow' },
  { label: 'This Weekend', value: 'weekend' },
  { label: 'This Week', value: 'week' },
  { label: 'All Upcoming', value: 'all' },
];

function isInDateRange(dateStr, range) {
  const ev = new Date(dateStr);
  const today = new Date(); today.setHours(0,0,0,0);
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const dayOfWeek = today.getDay();
  const saturday = new Date(today); saturday.setDate(today.getDate() + (6 - dayOfWeek));
  const sunday = new Date(saturday); sunday.setDate(saturday.getDate() + 1);
  const endOfWeek = new Date(today); endOfWeek.setDate(today.getDate() + (7 - dayOfWeek));
  const evDay = new Date(ev.getFullYear(), ev.getMonth(), ev.getDate());

  if (range === 'today') return evDay.getTime() === today.getTime();
  if (range === 'tomorrow') return evDay.getTime() === tomorrow.getTime();
  if (range === 'weekend') return evDay >= saturday && evDay <= sunday;
  if (range === 'week') return ev >= today && ev <= endOfWeek;
  return ev >= today;
}

function Events() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('id');
  const { city } = useCity();

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // 'upcoming' | 'all'
  const [searchTerm, setSearchTerm] = useState('');
  const [activeDateRange, setActiveDateRange] = useState('all');
  const [activeCategory, setActiveCategory] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rsvpStatus, setRsvpStatus] = useState({}); // eventId -> bool
  const [rsvpLoading, setRsvpLoading] = useState({});
  const [toast, setToast] = useState(null);

  const isLoggedIn = authService.isAuthenticated();

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Fetch events ──────────────────────────────────────────────────────────
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === 'upcoming'
        ? `${API_URL}/events?upcoming=true`
        : `${API_URL}/events`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        setEvents(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // ── Open focused event from URL param ─────────────────────────────────────
  useEffect(() => {
    if (focusId && events.length > 0) {
      const ev = events.find(e => String(e.id) === focusId);
      if (ev) setSelectedEvent(ev);
    }
  }, [focusId, events]);

  // ── Fetch RSVP statuses for logged-in user ────────────────────────────────
  useEffect(() => {
    if (!isLoggedIn || events.length === 0) return;
    const fetchStatuses = async () => {
      const results = await Promise.allSettled(
        events.map(e =>
          authService.get(`${API_URL}/events/${e.id}/rsvp`)
            .then(r => ({ id: e.id, rsvped: r.rsvped }))
            .catch(() => ({ id: e.id, rsvped: false }))
        )
      );
      const map = {};
      results.forEach(r => {
        if (r.status === 'fulfilled') map[r.value.id] = r.value.rsvped;
      });
      setRsvpStatus(map);
    };
    fetchStatuses();
  }, [events, isLoggedIn]);

  // ── RSVP toggle ───────────────────────────────────────────────────────────
  const toggleRsvp = async (eventId) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }
    setRsvpLoading(prev => ({ ...prev, [eventId]: true }));
    try {
      let res;
      if (rsvpStatus[eventId]) {
        res = await authService.delete(`${API_URL}/events/${eventId}/rsvp`);
        if (res.success) {
          setRsvpStatus(prev => ({ ...prev, [eventId]: false }));
          setEvents(prev =>
            prev.map(e => e.id === eventId
              ? { ...e, _count: { ...e._count, rsvps: Math.max(0, (e._count?.rsvps ?? 1) - 1) } }
              : e
            )
          );
          if (selectedEvent?.id === eventId) {
            setSelectedEvent(prev => ({
              ...prev,
              _count: { ...prev._count, rsvps: Math.max(0, (prev._count?.rsvps ?? 1) - 1) }
            }));
          }
          showToast('RSVP removed. Your club membership has been kept.');
        }
      } else {
        res = await authService.post(`${API_URL}/events/${eventId}/rsvp`);
        if (res.success) {
          setRsvpStatus(prev => ({ ...prev, [eventId]: true }));
          setEvents(prev =>
            prev.map(e => e.id === eventId
              ? { ...e, _count: { ...e._count, rsvps: (e._count?.rsvps ?? 0) + 1 } }
              : e
            )
          );
          if (selectedEvent?.id === eventId) {
            setSelectedEvent(prev => ({
              ...prev,
              _count: { ...prev._count, rsvps: (prev._count?.rsvps ?? 0) + 1 }
            }));
          }
          showToast("You're going! You've also been added to the club.");
        }
      }
      if (!res.success) showToast(res.message || 'Something went wrong', 'error');
    } catch (err) {
      showToast(err.message || 'Something went wrong', 'error');
    } finally {
      setRsvpLoading(prev => ({ ...prev, [eventId]: false }));
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  const formatShortDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric'
    });
  };

  const filtered = events.filter(e => {
    const searchOK = !searchTerm ||
      e.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (e.location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (e.club?.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const dateOK = isInDateRange(e.startDate, activeDateRange);
    const catOK = !activeCategory || (
      e.title.toLowerCase().includes(activeCategory) ||
      (e.description || '').toLowerCase().includes(activeCategory)
    );
    const cityOK = !city || (e.location || '').toLowerCase().includes(city.toLowerCase()) ||
      (e.club?.name || '').toLowerCase().includes(city.toLowerCase());
    return searchOK && dateOK && catOK && cityOK;
  });

  const isFull = (event) =>
    event.maxAttendees && (event._count?.rsvps ?? 0) >= event.maxAttendees;

  return (
    <div className="min-h-screen flex flex-col bg-base-200">
      <Navbar />

      {/* Hero banner */}
      <div className="bg-primary text-primary-content py-14 px-4">
        <div className="container mx-auto max-w-5xl">
          <h1 className="text-4xl font-extrabold mb-2">
            Upcoming Events {city && <span className="text-primary-content/70">in {city}</span>}
          </h1>
          <p className="text-primary-content/80 text-lg max-w-xl">
            Discover board game tournaments, casual game nights, and special community gatherings near you.
          </p>
        </div>
      </div>

      <div className="flex-grow container mx-auto px-4 max-w-6xl py-8">
        {/* Category Chips */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide mb-5 pb-0.5">
          <button
            onClick={() => setActiveCategory('')}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              !activeCategory ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-700 hover:border-primary hover:text-primary'
            }`}
          >
            All Categories
          </button>
          {CATEGORY_CHIPS.map(chip => (
            <button
              key={chip.value}
              onClick={() => setActiveCategory(activeCategory === chip.value ? '' : chip.value)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeCategory === chip.value ? 'bg-primary text-white border-primary' : 'border-gray-200 text-gray-700 hover:border-primary hover:text-primary'
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Date Chips + Search + Filter row */}
        <div className="flex flex-col gap-3 mb-6">
          {/* Date chips */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {DATE_CHIPS.map(chip => (
              <button
                key={chip.value}
                onClick={() => setActiveDateRange(chip.value)}
                className={`flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  activeDateRange === chip.value ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-600 hover:border-gray-900 hover:text-gray-900'
                }`}
              >
                {chip.label}
              </button>
            ))}
          </div>

          {/* Search + toggle row */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="flex rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
              {['upcoming', 'all'].map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    filter === f ? 'bg-primary text-primary-content' : 'bg-base-100 text-base-content/70 hover:bg-base-200'
                  }`}
                >
                  {f === 'upcoming' ? 'Upcoming' : 'All Events'}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Search events, locations or clubs…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="flex-1 border border-gray-200 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary bg-base-100"
            />
            <span className="text-sm text-gray-500 flex-shrink-0">
              {filtered.length} event{filtered.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow animate-pulse h-64" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow-sm">
            <svg className="mx-auto h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-500 text-lg font-medium">No events match your filters</p>
            <p className="text-gray-400 text-sm mt-1">Try a different date range, clear the search, or explore all events.</p>
            <div className="flex gap-3 justify-center mt-5">
              <button
                onClick={() => { setActiveDateRange('all'); setActiveCategory(''); setSearchTerm(''); setFilter('all'); }}
                className="px-6 py-2 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary/90"
              >
                Clear Filters
              </button>
              <button
                onClick={() => navigate('/partner-with-us')}
                className="px-6 py-2 border border-gray-200 text-gray-700 rounded-full text-sm font-semibold hover:border-primary hover:text-primary"
              >
                Host an Event
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((event) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-base-100 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col cursor-pointer"
                onClick={() => setSelectedEvent(event)}
              >
                {/* Image */}
                <div className="h-40 bg-primary relative overflow-hidden flex-shrink-0">
                  {event.imageUrl ? (
                    <img
                      src={event.imageUrl}
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-14 h-14 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute top-2 left-2 bg-white bg-opacity-90 rounded-lg px-2 py-1 text-center min-w-[48px]">
                    <div className="text-xs font-bold text-primary uppercase leading-none">
                      {new Date(event.startDate).toLocaleDateString('en-US', { month: 'short' })}
                    </div>
                    <div className="text-lg font-extrabold text-gray-900 leading-none">
                      {new Date(event.startDate).getDate()}
                    </div>
                  </div>
                  {isFull(event) && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      Full
                    </div>
                  )}
                  {rsvpStatus[event.id] && !isFull(event) && (
                    <div className="absolute top-2 right-2 bg-success text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      ✓ Going
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col flex-1">
                  <p className="text-xs text-primary font-semibold mb-1 truncate">{event.club?.name}</p>
                  <h3 className="text-base font-bold text-gray-900 mb-2 line-clamp-2 leading-snug">{event.title}</h3>

                  <div className="text-xs text-gray-500 space-y-1 mb-4">
                    <div className="flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {event.startTime}{event.endTime ? ` – ${event.endTime}` : ''}
                    </div>
                    {event.location && (
                      <div className="flex items-center gap-1.5 truncate">
                        <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                        {event.location}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5">
                      <svg className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {event._count?.rsvps ?? 0}{event.maxAttendees ? ` / ${event.maxAttendees}` : ''} going
                    </div>
                  </div>

                  <div className="mt-auto">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleRsvp(event.id); }}
                      disabled={rsvpLoading[event.id] || (isFull(event) && !rsvpStatus[event.id])}
                      className={`w-full py-2 rounded-lg text-sm font-semibold transition-all ${
                        rsvpStatus[event.id]
                          ? 'bg-primary/10 text-primary border border-primary/40 hover:bg-error/10 hover:text-error hover:border-error/40'
                          : isFull(event)
                          ? 'bg-base-200 text-base-content/40 cursor-not-allowed'
                          : 'bg-primary text-primary-content hover:bg-primary/90'
                      }`}
                    >
                      {rsvpLoading[event.id]
                        ? 'Loading…'
                        : rsvpStatus[event.id]
                        ? '✓ Going (click to remove)'
                        : isFull(event)
                        ? 'Event Full'
                        : "RSVP — I'm Going!"}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Event detail modal */}
      <AnimatePresence>
        {selectedEvent && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedEvent(null)}
          >
            <motion.div
              key="modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.2 }}
              onClick={e => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              {/* Image */}
              <div className="h-52 bg-primary relative rounded-t-2xl overflow-hidden">
                {selectedEvent.imageUrl ? (
                  <img src={selectedEvent.imageUrl} alt={selectedEvent.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <svg className="w-16 h-16 text-white opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="absolute top-4 right-4 bg-white bg-opacity-90 rounded-full p-1.5 text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-8">
                <p className="text-sm text-primary font-semibold mb-1">{selectedEvent.club?.name}</p>
                <h2 className="text-2xl font-bold text-gray-900 mb-5">{selectedEvent.title}</h2>

                <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{formatDate(selectedEvent.startDate)}</p>
                      {selectedEvent.endDate && selectedEvent.endDate.slice(0, 10) !== selectedEvent.startDate.slice(0, 10) && (
                        <p className="text-gray-500 text-xs">to {formatShortDate(selectedEvent.endDate)}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{selectedEvent.startTime}{selectedEvent.endTime ? ` – ${selectedEvent.endTime}` : ''}</p>
                    </div>
                  </div>

                  {selectedEvent.location && (
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{selectedEvent.location}</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedEvent._count?.rsvps ?? 0}
                        {selectedEvent.maxAttendees ? ` / ${selectedEvent.maxAttendees}` : ''} going
                      </p>
                      {isFull(selectedEvent) && <p className="text-red-500 text-xs">Event is full</p>}
                    </div>
                  </div>
                </div>

                {selectedEvent.description && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">About this event</h3>
                    <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">{selectedEvent.description}</p>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => toggleRsvp(selectedEvent.id)}
                    disabled={rsvpLoading[selectedEvent.id] || (isFull(selectedEvent) && !rsvpStatus[selectedEvent.id])}
                    className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all ${
                      rsvpStatus[selectedEvent.id]
                        ? 'bg-primary/10 text-primary border-2 border-primary/40 hover:bg-error/10 hover:text-error hover:border-error/40'
                        : isFull(selectedEvent)
                        ? 'bg-base-200 text-base-content/40 cursor-not-allowed'
                        : 'bg-primary text-primary-content hover:bg-primary/90'
                    }`}
                  >
                    {rsvpLoading[selectedEvent.id]
                      ? 'Loading…'
                      : rsvpStatus[selectedEvent.id]
                      ? "✓ You're Going (click to remove RSVP)"
                      : isFull(selectedEvent)
                      ? 'Event Full'
                      : "RSVP — I'm Going!"}
                  </button>
                  {selectedEvent.club?.slug && (
                    <button
                      onClick={() => navigate(`/clubs/${selectedEvent.club.slug}`)}
                      className="px-5 py-3 rounded-xl text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                    >
                      View Club
                    </button>
                  )}
                </div>

                {!isLoggedIn && (
                  <p className="text-center text-xs text-gray-400 mt-3">
                    <button onClick={() => navigate('/login')} className="text-primary font-medium hover:underline">Login</button> or{' '}
                    <button onClick={() => navigate('/register')} className="text-primary font-medium hover:underline">register</button> to RSVP
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full text-sm font-semibold shadow-lg z-50 ${
              toast.type === 'error' ? 'bg-error text-error-content' : 'bg-primary text-primary-content'
            }`}
          >
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </div>
  );
}

export default Events;
